/**
 * Status consolidado por estrutura (Barragens A/B/C) — GeoGuard UPM.
 *
 * TypeScript PURO (regra de domínio #1): sem React, Supabase ou DOM.
 * Espelha `computeStructureStatus` / `STATUS_UI` do protótipo v5.10
 * (docs/reference/GeoGuard_UPM_v5_10_0_8.html, ~L1661-1688).
 *
 * Este status é uma TERCEIRA dimensão, distinta de:
 *  - `TarpLevel`      → classificação de UM instrumento (Carta de Controle);
 *  - `NivelEmergencia`→ emergência PAEBM formal (NE-1/2/3, Coordenador PAEBM).
 *
 * Regra central de segurança: o cálculo AUTOMÁTICO nunca ultrapassa
 * `OBSERVACAO` — sem exceção. Instrumento em nível / sem sinal / TARP não
 * baixada colocam a estrutura em Observação (nível de controle, PRÉVIO aos
 * níveis ANM). Subir para nível ANM é ATO do RT, via o enquadramento da BAIXA da
 * TARP (`declaracaoAnmAtivaPorBarragem`). Coerente com operador read-only
 * (ADR-0009).
 *
 * GUPM-291 fechou a última exceção: até então uma TARP aberta cujo fluxo atingia
 * o terminal `paebm` promovia a estrutura a `EMERGENCIA` na hora (GUPM-207). O
 * dono reverteu — acionar protocolo não é declarar nível. Consequência a decidir:
 * como `NE_TO_NIVEL` mapeia o pior enquadramento (`NE-3`) para `CRITICO`,
 * `EMERGENCIA` deixou de ter qualquer via de publicação no semáforo. O estado
 * segue no tipo (e no `STATUS_ESTRUTURA_UI`) aguardando essa decisão; o campo
 * `emergencia` abaixo permanece como encaixe, hoje sem alimentador.
 */

/** Os 6 estados do protótipo v5.10 (STATUS_UI). */
export type StatusEstrutura =
  | 'NORMAL'
  | 'OBSERVACAO'
  | 'ATENCAO'
  | 'ALERTA'
  | 'CRITICO'
  | 'EMERGENCIA';

/** Níveis ANM que só o RT pode declarar (subconjunto dos estados). */
export type NivelAnmDeclaravel = Extract<StatusEstrutura, 'ATENCAO' | 'ALERTA' | 'CRITICO'>;

/** Declaração de nível ANM feita pelo RT (rodada TARP alimenta isto). */
export interface DeclaracaoAnm {
  nivel: NivelAnmDeclaravel;
  declaradoPor?: string;
  declaradoEm?: Date;
  motivo?: string;
}

/**
 * Entradas do cálculo. Só `instrumentosEmNivel` é alimentado por dado real
 * nesta rodada; os demais são "encaixes" tipados (default vazio) que ligam nas
 * rodadas de Instrumentação (perda de sinal) e TARP (TARPs, declaração ANM,
 * emergência) sem reescrever o domínio.
 */
export interface StatusEstruturaInput {
  /** Instrumentos da estrutura com nível ≠ NORMAL pela Carta (real hoje). */
  instrumentosEmNivel?: number;
  /** Instrumentos sem sinal (encaixe — Instrumentação). */
  instrumentosSemSinal?: number;
  /** TARPs abertas não baixadas (encaixe — TARP). */
  tarpsAbertas?: number;
  /** TARPs em análise pelo RT (encaixe — TARP). */
  tarpsEmAnaliseRt?: number;
  /** Declaração de nível ANM do RT (encaixe — TARP). `null`/ausente = nenhuma. */
  declaracaoAnm?: DeclaracaoAnm | null;
  /**
   * Gatilho de emergência (encaixe). **Sem alimentador desde o GUPM-291**: a TARP
   * aberta em `paebm` deixou de ligar isto. Só religar com decisão do dono — foi
   * exatamente essa promoção automática que ele mandou remover.
   */
  emergencia?: boolean;
}

export interface StatusEstruturaResult {
  nivel: StatusEstrutura;
  /** `true` apenas em OBSERVAÇÃO — sinaliza "nível de controle, prévio à ANM". */
  pre: boolean;
  /** Texto curto explicando a origem do status (espelha `fonte` do protótipo). */
  fonte: string;
}

/** Há alguma ocorrência automática que justifica Observação? */
function temOcorrenciaAutomatica(i: StatusEstruturaInput): boolean {
  return (
    (i.instrumentosEmNivel ?? 0) > 0 ||
    (i.instrumentosSemSinal ?? 0) > 0 ||
    (i.tarpsAbertas ?? 0) > 0 ||
    (i.tarpsEmAnaliseRt ?? 0) > 0
  );
}

/**
 * Calcula o status consolidado de UMA estrutura, com a precedência do v5.10:
 *  1) emergência → EMERGENCIA
 *  2) declaração ANM do RT → o nível declarado
 *  3) qualquer ocorrência automática → OBSERVACAO (pré-ANM)
 *  4) senão → NORMAL
 */
export function computeStatusEstrutura(input: StatusEstruturaInput): StatusEstruturaResult {
  if (input.emergencia) {
    return { nivel: 'EMERGENCIA', pre: false, fonte: 'Emergência / ruptura em curso' };
  }
  if (input.declaracaoAnm) {
    return {
      nivel: input.declaracaoAnm.nivel,
      pre: false,
      fonte: 'Nível ANM declarado por avaliação técnica',
    };
  }
  if (temOcorrenciaAutomatica(input)) {
    return { nivel: 'OBSERVACAO', pre: true, fonte: 'Nível de controle (prévio aos níveis ANM)' };
  }
  return { nivel: 'NORMAL', pre: false, fonte: 'Sem ocorrências ativas' };
}

/**
 * Nível exibível no semáforo. `OBSERVACAO` fica de fora de propósito: é estado
 * operacional, não nível da estrutura.
 */
export type NivelAnmExibido = Exclude<StatusEstrutura, 'OBSERVACAO'>;

/**
 * Nível ANM da estrutura para o semáforo — colapsa `OBSERVACAO` em `NORMAL`.
 *
 * A estrutura só entra em nível quando o RT declara (ATENÇÃO/ALERTA/CRÍTICO) ou
 * na emergência, que é direta. Ocorrência automática — instrumento fora da faixa
 * da Carta, perda de sinal, TARP aberta — é nível de CONTROLE, prévio à ANM: ela
 * abre TARP e aparece no chip do header, nos contadores por nível e no bloco
 * "Precisa da sua atenção", mas não declara nível de barragem. Pintar o semáforo
 * a partir dela publicaria um nível ANM que nenhuma avaliação técnica sustenta.
 *
 * Pelo desenho de `computeStatusEstrutura`, ATENÇÃO/ALERTA/CRÍTICO só existem via
 * `declaracaoAnm` e EMERGÊNCIA só via `emergencia` — logo colapsar `OBSERVACAO`
 * basta, sem precisar reinspecionar a origem do status aqui.
 */
export function nivelAnmSemaforo(status: StatusEstrutura): NivelAnmExibido {
  return status === 'OBSERVACAO' ? 'NORMAL' : status;
}

/** Ordem de severidade (menor = mais brando). Fonte para consolidação/ordenação. */
const ORDEM_SEVERIDADE: readonly StatusEstrutura[] = [
  'NORMAL',
  'OBSERVACAO',
  'ATENCAO',
  'ALERTA',
  'CRITICO',
  'EMERGENCIA',
];

/** Índice de severidade de um status (NORMAL=0 … EMERGENCIA=5). */
export function severidadeStatus(s: StatusEstrutura): number {
  return ORDEM_SEVERIDADE.indexOf(s);
}

/** Retorna o mais severo entre dois status — reducer da consolidação A/B/C (dam scope 156). */
export function piorStatus(a: StatusEstrutura, b: StatusEstrutura): StatusEstrutura {
  return severidadeStatus(b) > severidadeStatus(a) ? b : a;
}
